import React, { useState } from "react";
import { differenceBy } from "lodash";
import { ScheduleLayer } from "@sonder/ui/source/styled/components/ScheduleLayer";
import { defaultScheduleLayerRenderFn } from "@sonder/ui/source/styled/components/ScheduleLayer/ScheduleLayer";
import { ScheduleLayoutWithNode } from "@sonder/ui/source/styled/components/ScheduleLayer/helpers";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { useUpdateTask } from "core/queries/UpdateTask";
import { closeItem, setModifyingTask } from "schedule/actions";
import { useStore } from "schedule/store";

interface StackedScheduleLayerProps {
  layout: ScheduleLayoutWithNode[];
}

export const StackedScheduleLayer = ({ layout }: StackedScheduleLayerProps) => {
  const errorReporter = useErrorReporterFiltered();
  const [cachedTime, setCachedTime] = useState(new Date());
  const {
    dispatch,
    state: { dateRange, isDisabled, isSelecting, viewedItems },
  } = useStore();
  const [onUpdateTask] = useUpdateTask({
    refetchQueries: [],
  });
  const onReset = () => {
    requestAnimationFrame(() => {
      setCachedTime(new Date());
    });
  };
  const onLayerChange = async (newLayout: ScheduleLayoutWithNode[]) => {
    // React-Grid-Layout gives us all the nodes, we need to find the changed ones. :)
    const changedNodes = differenceBy(
      newLayout,
      layout,
      item =>
        `${item.startAt.clone().startOf("minute").toISOString()}-
        ${item.endAt.clone().endOf("minute").toISOString()}`,
    ).filter(l => l.isDraggable);
    if (changedNodes.length)
      if (changedNodes.length > 1) {
        // TODO: Bulk Schedule Updates?
        errorReporter.notify(
          new Error(
            `SchedulerRow updating multiple nodes: ${changedNodes
              .map(n => n.i)
              .join(", ")}`,
          ),
        );
        onReset();
      } else {
        const firstNode = changedNodes.shift();
        try {
          if (firstNode)
            await onUpdateTask({
              variables: {
                taskId: firstNode.i,
                newScheduledStartAt: firstNode.startAt,
                dueBy: firstNode.endAt,
              },
            });
        } catch (error) {
          errorReporter.notify(error);
          onReset();
        }
      }
  };
  const rerenderCache = [
    isDisabled,
    isSelecting,
    cachedTime.toString(),
    ...layout.map(t => `${t.startAt}-${t.endAt}-${t.status}-${t.shuffled}`),
  ];
  return (
    <ScheduleLayer
      endAt={dateRange.endAt}
      layout={layout}
      rerenderCache={rerenderCache}
      startAt={dateRange.startAt}
      onChange={onLayerChange}
      onChangeStart={newItem => {
        if (newItem) dispatch(setModifyingTask(newItem.i));

        dispatch(closeItem(viewedItems));
      }}
      onChangeStop={() => {
        dispatch(setModifyingTask(""));
      }}
    >
      {({ cols, isModifying, layout: gridLayout }) =>
        defaultScheduleLayerRenderFn({
          cols,
          isModifying,
          layout: gridLayout,
        })
      }
    </ScheduleLayer>
  );
};
